import type { TaskStateLocal } from 'shared/modelTypes/client';
import { uploadTaskSubmission } from './api';

// NOTE: attempting to load indexedDB with a File object stored crashes Safari,
// so we convert the file to a StoredFile and then convert it back to a File when we need to upload it
type StoredFile = {
	name: string;
	type: string;
	size: number;
	buffer: ArrayBuffer;
};

interface QueuedSubmission {
	id: string;
	eventId: string;
	submission: TaskStateLocal;
	file?: StoredFile;
	videoId?: string | null;
	timestamp: number;
}

async function convertFileToStoredFile(file: File): Promise<StoredFile> {
	return {
		name: file.name,
		type: file.type,
		size: file.size,
		buffer: await file.arrayBuffer(),
	};
}

async function convertStoredFileToFile(storedFile: StoredFile): Promise<File> {
	return new File([storedFile.buffer], storedFile.name, { type: storedFile.type });
}

function promisify<T>(request: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error);
	});
}

export class OfflineSubmissionQueue {
	private db: IDBDatabase | null = null;
	private readonly dbName = 'OfflineSubmissionQueue';
	private readonly storeName = 'submissions';
	private readonly eventId: string;
	private readonly teamId: string;

	constructor(eventId: string, teamId: string) {
		this.eventId = eventId;
		this.teamId = teamId;
		this.initDatabase();
	}

	private async initDatabase(): Promise<void> {
		return new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, 1);

			request.onerror = () => reject(request.error);
			request.onsuccess = () => {
				this.db = request.result;
				// Clear submissions from other events after database is ready
				this.clearPreviousEventSubmissions();
				resolve();
			};

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;

				// Create object store for submissions (including files)
				const submissionStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
				submissionStore.createIndex('eventId', 'eventId', { unique: false });
				submissionStore.createIndex('taskId', 'submission.taskId', { unique: false });
				submissionStore.createIndex('timestamp', 'timestamp', { unique: false });
			};
		});
	}

	private async ensureDatabase(): Promise<IDBDatabase> {
		if (!this.db) {
			await this.initDatabase();
		}
		return this.db!;
	}

	private async getStore(mode: 'readonly' | 'readwrite' = 'readwrite') {
		const db = await this.ensureDatabase();
		const transaction = db.transaction([this.storeName], mode);
		return transaction.objectStore(this.storeName);
	}

	/**
	 * Add a task to the offline queue so it can be submitted at a later date
	 * @param submission the submission
	 * @param file the file to upload alongside it
	 * @param videoId the videoId of a video that was already uploaded successfully
	 */
	async add(submission: TaskStateLocal, file?: File, videoId?: string | null): Promise<void> {
		const storedFile = file ? await convertFileToStoredFile(file) : undefined;
		const store = await this.getStore();
		const all = await promisify(store.getAll());

		const exists = all.some((item: QueuedSubmission) => item.submission.taskId === submission.taskId);

		if (exists) {
			console.warn('Submission already queued:', submission.taskId);
			return;
		}

		const queuedSubmission: QueuedSubmission = {
			id: `${submission.taskId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			eventId: this.eventId,
			submission,
			file: videoId ? undefined : storedFile,
			videoId: videoId,
			timestamp: Date.now(),
		};
		await promisify(store.add(queuedSubmission));
	}

	/**
	 * remove a submission from the queue
	 * @param taskId the taskId of the submission to remove
	 */
	async remove(taskId: string): Promise<void> {
		const store = await this.getStore();

		// Find submissions with this taskId
		const index = store.index('taskId');
		const submissions = (await promisify(index.getAll(taskId))) as QueuedSubmission[];

		// Remove each submission
		await Promise.all(submissions.map((submission) => promisify(store.delete(submission.id))));
	}

	/**
	 * Clears all the tasks
	 */
	async clear(): Promise<void> {
		const store = await this.getStore();
		await promisify(store.clear());
	}

	/**
	 * Get all queued submissions for this event
	 */
	private async getAllSubmissions(): Promise<QueuedSubmission[]> {
		const store = await this.getStore('readonly');
		const index = store.index('eventId');
		return promisify(index.getAll(this.eventId)) as Promise<QueuedSubmission[]>;
	}

	/**
	 * Remove a specific submission by ID
	 */
	private async removeSubmission(submissionId: string): Promise<void> {
		const store = await this.getStore();
		await promisify(store.delete(submissionId));
	}

	/**
	 * Attempts to submit all the offline tasks to the server
	 * and removes them from the queue if it succeeds
	 */
	async trySubmitAll(): Promise<void> {
		const submissions = await this.getAllSubmissions();

		console.log('trying to submit', submissions.length, 'offline tasks');

		for (const queuedSubmission of submissions) {
			console.log('trying to submit', queuedSubmission.submission.taskId);
			try {
				const file = queuedSubmission.file ? await convertStoredFileToFile(queuedSubmission.file) : undefined;
				const result = await uploadTaskSubmission(
					{
						eventId: this.eventId,
						teamId: this.teamId,
						submission: queuedSubmission.submission,
					},
					file,
					queuedSubmission.videoId,
					false,
				);

				if (result.success) {
					// Remove from queue on successful submission
					await this.removeSubmission(queuedSubmission.id);
					console.log(`Successfully submitted offline task: ${queuedSubmission.submission.taskId}`);
				} else {
					console.warn(`Failed to submit offline task: ${queuedSubmission.submission.taskId}`);
				}
			} catch (error) {
				console.error(`Error submitting offline task ${queuedSubmission.submission.taskId}:`, error);
			}
		}
	}

	/**
	 * Get the count of queued submissions
	 */
	async getQueueSize(): Promise<number> {
		const submissions = await this.getAllSubmissions();
		return submissions.length;
	}

	/**
	 * Check if there are any queued submissions
	 */
	async hasQueuedSubmissions(): Promise<boolean> {
		const size = await this.getQueueSize();
		return size > 0;
	}

	/**
	 * clears any submissions that are for an event other than the current one
	 * because if you are no longer in that event you will never submit those tasks
	 */
	private async clearPreviousEventSubmissions(): Promise<void> {
		try {
			const store = await this.getStore();
			const index = store.index('eventId');
			const allKeys = (await promisify(index.getAllKeys())) as string[];

			// Get submissions that don't match current eventId
			const promises = allKeys.map(async (key) => {
				const submission = (await promisify(store.get(key))) as QueuedSubmission | null;

				// Delete if submission exists and doesn't match current eventId
				if (submission && submission.eventId !== this.eventId) {
					await promisify(store.delete(key));
				}
			});

			await Promise.all(promises);
		} catch (error) {
			console.warn('Failed to clear submissions from other events:', error);
		}
	}
}
